import { createSlice } from "@reduxjs/toolkit";
import { Organization } from "../../@types/organization"
import { User } from "../../@types/user";
import { Profile } from "../../@types/profile";
import { findOrganizations } from "./actions/findOrganizations";
import { findProfileOrganization } from "./actions/findProfileOrganizations";
import { assignOrganizations } from "./actions/assignOrganizations";

interface UserProfileOrganizationState {
  user: User;
  profile: Profile;
  organizations: Organization[];
  findOrganizationsStatus: "idle" | "pending" | "succeeded" | "failed";
  findProfileOrganizationStatus: "idle" | "pending" | "succeeded" | "failed";
  assignOrganizationsStatus: "idle" | "pending" | "succeeded" | "failed";
  searchText: string;
  selectedSearchText: string;
  selectedOrganizations: Organization[];
  assignedOrganizations: 
  {
    createdAt: number;
    updatedAt: number;
    id: number;
    user: number;
    profile: number;
    organization: Organization;
  }[];
  maxListAvailableIndex: number; //Para listar con IonInfiniteScroll
};

const initialState = {
  user: {} as User,
  profile: {} as Profile,
  organizations: [],
  findOrganizationsStatus: "idle",
  findProfileOrganizationStatus: "idle",
  assignOrganizationsStatus: "idle",
  searchText: "",
  selectedSearchText: "",
  selectedOrganizations: [],
  assignedOrganizations: [],
  maxListAvailableIndex: 20,
} as UserProfileOrganizationState;


const userProfileOrganizationSlice = createSlice({
  name: "userProfileOrganization",
  initialState,
  reducers: {
    setInitialData(state) {
      return initialState;
    },
    setUser(state, { payload }) {
      state.user = payload;
    },
    setProfile(state, { payload }) {
      state.profile = payload;
    },
    setSearchText(state, { payload }) { 
      state.searchText = payload;
      state.maxListAvailableIndex = 20; 
    },
    setSelectedSearchText(state, { payload }){
      state.selectedSearchText = payload;
    },
    setMaxListAvailableIndex(state, { payload }) {
      state.maxListAvailableIndex = payload;
    },
    setPreselectOrganization(state, { payload }) {
      state.selectedOrganizations = [...state.selectedOrganizations, payload];
    },
    setPreselectAll(state, { payload }) {
      state.selectedOrganizations = [...state.selectedOrganizations, ...payload];
    },
    setDeselectOrganization(state, { payload }) {
      state.selectedOrganizations = state.selectedOrganizations.filter(({ id }) => id !== payload.id);
    },
    setDeselectAll(state) {
      state.selectedOrganizations = [];
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(findOrganizations.pending, (state) => {
        state.findOrganizationsStatus = "pending";
        state.organizations = [];
      })
      .addCase(findOrganizations.fulfilled, (state, { payload }) => {
        state.findOrganizationsStatus = "succeeded";
        state.organizations = payload;
      })
      .addCase(findOrganizations.rejected, (state) => {
        state.findOrganizationsStatus = "failed";
        state.organizations = [];
      })
      .addCase(findProfileOrganization.pending, (state) => {
        state.findProfileOrganizationStatus = "pending";
        state.selectedOrganizations = [];
        state.assignedOrganizations = [];
      })
      .addCase(findProfileOrganization.fulfilled, (state, { payload }) => {
        state.findProfileOrganizationStatus = "succeeded";
        state.selectedOrganizations = payload.map((upo: {organization: Organization}) => { 
            return {id: upo.organization.id, name: upo.organization.name}
        });
        state.assignedOrganizations = payload;
      })
      .addCase(findProfileOrganization.rejected, (state) => {
        state.findProfileOrganizationStatus = "failed";
        state.selectedOrganizations = [];
        state.assignedOrganizations = [];
      })
      .addCase(assignOrganizations.pending, (state) => {
        state.assignOrganizationsStatus = "pending";
      })
      .addCase(assignOrganizations.fulfilled, (state) => {
        state.assignOrganizationsStatus = "succeeded";
      })
      .addCase(assignOrganizations.rejected, (state) => {
        state.assignOrganizationsStatus = "failed";
      });
  },
});

export const {
  setInitialData,
  setUser,
  setProfile,
  setSearchText,
  setSelectedSearchText,
  setMaxListAvailableIndex,
  setPreselectAll,
  setPreselectOrganization,
  setDeselectAll,
  setDeselectOrganization,
} = userProfileOrganizationSlice.actions

export default userProfileOrganizationSlice.reducer